import { useCatalog } from '@/api/queries';
import type { PlanVibe, PlannerChoice } from '@/api/types';

// "Plan our date" content (SPEC_V6_2 §2). The server's catalog
// (`GET /v1/catalog.date_planner`) wins; these mirror it for older payloads.

export const VIBES: { id: PlanVibe; label: string; emoji: string; line: string }[] = [
  { id: 'cozy', label: 'Cozy', emoji: '☕', line: 'Slow, warm, stories' },
  { id: 'playful', label: 'Playful', emoji: '🎲', line: 'Games, quick laughs' },
  { id: 'curious', label: 'Curious', emoji: '🔭', line: 'Big questions, real answers' },
];

export const SPICE: { level: number; label: string; line: string }[] = [
  { level: 0, label: 'Keep it sweet', line: 'Wholesome only. Grandma could watch.' },
  { level: 1, label: 'A little flirty', line: 'A wink here and there.' },
  { level: 2, label: 'Bring some heat', line: 'Bolder questions are welcome.' },
  { level: 3, label: 'Spicy, please', line: 'The spiciest we’ve got (still kind).' },
];

export const FALLBACK_PROPS: PlannerChoice[] = [
  { id: 'chai', label: 'chai', emoji: '☕' },
  { id: 'snack', label: 'a snack', emoji: '🍕' },
  { id: 'playlist', label: 'a playlist', emoji: '🎧' },
  { id: 'pet', label: 'a pet cameo', emoji: '🐶' },
  { id: 'window', label: 'a window view', emoji: '🌧️' },
  { id: 'surprise', label: 'a surprise', emoji: '🎲' },
];

export const FALLBACK_TOPICS: PlannerChoice[] = [
  { id: 'food', label: 'Food', emoji: '🍜' },
  { id: 'travel', label: 'Travel', emoji: '✈️' },
  { id: 'music', label: 'Music', emoji: '🎧' },
  { id: 'movies', label: 'Movies & shows', emoji: '🎬' },
  { id: 'books', label: 'Books', emoji: '📚' },
  { id: 'pets', label: 'Pets', emoji: '🐶' },
  { id: 'sports', label: 'Sports', emoji: '⚽' },
  { id: 'nature', label: 'Nature', emoji: '🌿' },
  { id: 'tech', label: 'Tech', emoji: '💻' },
  { id: 'art', label: 'Making things', emoji: '🎨' },
  { id: 'childhood', label: 'Childhood memories', emoji: '🧸' },
  { id: 'dreams', label: 'Big dreams', emoji: '🌠' },
  { id: 'people', label: 'Friends & family', emoji: '👯' },
  { id: 'values', label: 'What matters to us', emoji: '🧭' },
  { id: 'learning', label: 'Learning stuff', emoji: '🧠' },
  { id: 'culture', label: 'Culture & festivals', emoji: '🪔' },
];

export function usePlannerCatalog(): { props: PlannerChoice[]; topics: PlannerChoice[] } {
  const c = useCatalog().data?.date_planner;
  return { props: c?.props?.length ? c.props : FALLBACK_PROPS, topics: c?.topics?.length ? c.topics : FALLBACK_TOPICS };
}

export const vibeOf = (id?: string) => VIBES.find((v) => v.id === id) ?? null;
export const choiceOf = (list: PlannerChoice[], id?: string) => (id ? (list.find((c) => c.id === id) ?? null) : null);

/** "chai": the label only; the catalog's emoji stays in the data, not in the chrome. */
export const propText = (list: PlannerChoice[], id?: string) => choiceOf(list, id)?.label ?? null;
